import { ChevronDown } from "lucide-react";
import { FAQToggle } from "@/components/faq/faq-toggle";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

export type FAQItemProps = {
  itemId: string;
  title: string;
  content: ReactNode;
  showText?: string;
  hideText?: string;
  compact?: boolean;
  variant?: "cards" | "rows";
};

export function FAQItem({
  itemId,
  title,
  content,
  showText,
  hideText,
  compact,
  variant = "cards",
}: FAQItemProps) {
  if (variant === "rows")
    return (
      <details className="group border-b last:border-b-0">
        <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 py-4 text-left text-sm font-medium [&::-webkit-details-marker]:hidden">
          <span>{title}</span>
          <ChevronDown
            aria-hidden
            className="size-4 shrink-0 transition-transform group-open:rotate-180 motion-reduce:transition-none"
          />
        </summary>
        <div
          id={itemId}
          className="prose prose-sm prose-neutral max-w-full pb-5 pr-6 leading-relaxed dark:prose-invert [&_p]:text-muted-foreground"
        >
          {content}
        </div>
      </details>
    );
  return (
    <div
      className={cn("flex flex-col border rounded-lg", {
        "p-6 gap-4": !compact,
        "p-4 gap-2": compact,
      })}
    >
      <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
        <h3
          className={cn("font-medium", {
            "text-lg md:text-2xl": !compact,
            "text-base md:text-lg": compact,
          })}
        >
          {title}
        </h3>

        <FAQToggle itemId={itemId} showText={showText} hideText={hideText} />
      </div>

      <div
        id={itemId}
        className="prose prose-neutral w-full max-w-full dark:prose-invert [&_p]:text-muted-foreground"
        style={{ display: "none" }}
      >
        {content}
      </div>
    </div>
  );
}

FAQItem.displayName = "FAQItem";
